import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';
import InputMask from 'react-input-mask';
import bancos from './data/bancos.json';
import ErrorModal from '../components/ErrorModal';

function MultiStepForm() {
  const [step, setStep] = useState(1);
  const [simulationOptions, setSimulationOptions] = useState([]);
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [selectedSimulation, setSelectedSimulation] = useState(null);
  const [correlationId, setCorrelationId] = useState('');
  const [errorModal, setErrorModal] = useState({ show: false, title: '', message: '' });

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [formData, setFormData] = useState({
    name: '',
    cpf: '',
    email: '',
    celular: '',
    numeroInstalacao: '',
    // Estes campos serão enviados como 0 para a simulação
    valorFinanciado: 0,
    qtdParcelas: 0,
    // Endereço e dados pessoais
    cdCepRes: '',
    enderecoRes: '',
    numeroRes: '',
    bairroRes: '',
    cidadeRes: '',
    ufRes: '',
    // Informações bancárias
    cdBanco: '',
    cdAgencia: '',
    cdConta: '',
    valorRenda: '',
    // Documentos
    nomeMae: '',
    cdRg: '',
    ufEmissorRg: '',
    emissorRg: '',
    dataExpedicaoRg: '',
    sexo: '',          // novo campo
    estadoCivil: '',   // novo campo
    naturalidade: '',
    nacionalidade: '',
    dataNascimento: '',
  });

  const [csrfToken, setCsrfToken] = useState('');

  useEffect(() => {
    axios
      .get(`${apiBaseUrl}/api/csrf-token`)
      .then((response) => {
        setCsrfToken(response.data.csrfToken);
      })
      .catch((error) => {
        console.error('Erro ao buscar o CSRF token:', error);
      });
  }, [apiBaseUrl]);

  const handleSendRequest = async () => {
    setSimulationLoading(true);
    const payload = {
      cpf: formData.cpf,
      nome: formData.name,
      telefone: formData.celular,
      dataNascimento: formData.dataNascimento,
      numeroInstalacao: formData.numeroInstalacao,
      confirmed: false,         // sinaliza etapa 1
    };
    try {
      const response = await axios.post(
        `${apiBaseUrl}/api/simulate-loan`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data.step === 'sms') {
        setCorrelationId(response.data.correlationId || '');
        setStep(2);
      } else if (response.data.error) {
        // Se a API retornar erro, mostrar popup
        let errorMessage = response.data.error;
        let detailMsg = response.data.detail;
        
        // Tenta extrair a mensagem do JSON se possível
        if (detailMsg) {
          try {
            const detailObj = JSON.parse(detailMsg);
            if (detailObj.Mensagem) {
              // Extrai apenas a mensagem limpa, removendo partes técnicas
              const mensagem = detailObj.Mensagem;
              const match = mensagem.match(/"([^"]*?)"/);
              if (match && match[1]) {
                errorMessage = match[1];
              } else {
                errorMessage = mensagem;
              }
            }
          } catch {
            // Se não conseguir fazer parse, mantém a mensagem original
          }
        }
        
        // Mostrar modal de erro
        setErrorModal({
          show: true,
          title: 'Erro',
          message: errorMessage,
        });
      } else {
        // Fluxo normal/se já vier simulações
        setSimulationOptions(response.data.simulacoes?.simulacao || []);
        setStep(3);
      }
    } catch (error) {
      // Se for erro de rede ou exceção
      let errorMessage = error.response?.data?.error || error.message;
      let detail = error.response?.data?.detail;
      
      if (detail) {
        try {
          const detailObj = JSON.parse(detail);
          if (detailObj.Mensagem) {
            // Extrai apenas a mensagem limpa, removendo partes técnicas
            const mensagem = detailObj.Mensagem;
            const match = mensagem.match(/"([^"]*?)"/);
            if (match && match[1]) {
              errorMessage = match[1];
            } else {
              errorMessage = mensagem;
            }
          }
        } catch {
          // Se não conseguir fazer parse, mantém a mensagem original
        }
      }
      
      // Mostrar modal de erro
      setErrorModal({
        show: true,
        title: 'Erro ao Enviar Solicitação',
        message: errorMessage,
      });
      console.error('Erro ao enviar solicitação:', error);
    } finally {
      setSimulationLoading(false);
    }
  };

  // 2ª chamada: confirma SMS e prossegue para simulação
  const handleConfirmSms = async () => {
    setSimulationLoading(true);
    const payload = {
      cpf: formData.cpf,
      nome: formData.name,
      telefone: formData.celular,
      dataNascimento: formData.dataNascimento,
      numeroInstalacao: formData.numeroInstalacao,
      correlationId,
      confirmed: true,           // sinaliza etapa 2
    };
    try {
      const response = await axios.post(
        `${apiBaseUrl}/api/simulate-loan`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      setSimulationOptions(response.data.simulacoes?.simulacao || []);
      setStep(3);
    } catch (error) {
      console.error('Erro ao confirmar SMS:', error);
    } finally {
      setSimulationLoading(false);
    }
  };

  const handleNext = () => {
    if (step === 1) {
      handleSendRequest();
    } else {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    if (step === 3 && simulationOptions.length === 0) {
      setStep(1); // Voltar para a primeira tela se não houver propostas
    } else {
      setStep(step - 1);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCepChange = async (e) => {
    const cep = e.target.value.replace(/\D/g, '');
    setFormData({ ...formData, cdCepRes: cep });

    if (cep.length === 8) {
      try {
        const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
        if (response.data) {
          setFormData((prevData) => ({
            ...prevData,
            enderecoRes: response.data.logradouro,
            bairroRes: response.data.bairro,
            cidadeRes: response.data.localidade,
            ufRes: response.data.uf,
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar os dados do CEP:', error);
      }
    }
  };

  const handleSimulationSelect = (e) => {
    const selectedIndex = e.target.value;
    const selectedOption = simulationOptions[selectedIndex];
    setSelectedSimulation(selectedOption);
    setFormData({
      ...formData,
      valorFinanciado: selectedOption.valorFinanciado,
      qtdParcelas: selectedOption.quantidadeParcelas,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData, { selectedSimulation });
    axios
      .post(
        `${apiBaseUrl}/api/submit-loan`,
        { ...formData, selectedSimulation },
        {
          withCredentials: true,
          headers: {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'application/json',
          },
        }
      )
      .then((response) => {
        console.log('Dados enviados com sucesso:', response.data);
        window.location.href = 'https://finmatchbr.com/master-luz-finmatch/obrigado';
      })
      .catch((error) => {
        console.error('Erro ao enviar os dados:', error);
      });
  };

  // Função para fechar o modal de erro
  const handleCloseModal = () => {
    setErrorModal({ show: false, title: '', message: '' });
  };

  return (
    <div className="row d-flex justify-content-center align-items-center min-vh-100">
      <div
        className="d-none d-md-flex justify-content-center align-items-center col-6 min-vh-100"
        style={{ backgroundColor: '#593572' }}
      >
        <div className="container d-block justify-content-center align-items-center w-75">
          <div className="text-center">
            <img src="/images/logo-FinMatch-brnaco.png" alt="FinMatch" className="img-fluid h-auto" />
          </div>
          <br />
          <h5 className="fw-bolder fs-1 text-white text-center">
            Complete seu cadastro e veja sua oferta de crédito personalizada em minutos
          </h5>
        </div>
      </div>
      <div className="col-md-6 col-12 px-5">
        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <div>
              {simulationLoading ? (
                <div className="text-center mt-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p className="mt-2">Aguarde isso pode levar 2 minutos</p>
                </div>
              ) : (
                <>
                  <h4>Informe dados e nº da conta <strong>CPFL/RGE</strong>. Em minutos, link por SMS para seguir na análise.</h4>
                  <div className="form-row row">
                    <div className="form-group mb-3">
                      <label>Nome</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label>Email</label>
                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label>Data de Nascimento</label>
                      <input
                        type="date"
                        name="dataNascimento"
                        className="form-control"
                        value={formData.dataNascimento}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group col-6 mb-3">
                      <label>CPF</label>
                      <InputMask
                        mask="999.999.999-99"
                        name="cpf"
                        className="form-control"
                        value={formData.cpf}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group col-6 mb-3">
                      <label>Celular</label>
                      <InputMask
                        mask="(99) 99999-9999"
                        name="celular"
                        className="form-control"
                        value={formData.celular}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group mb-3">
                      <label>Número de Instalação (Apenas CPFL ou RGE)</label>
                      <input
                        type="text"
                        name="numeroInstalacao"
                        className="form-control"
                        value={formData.numeroInstalacao}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="row mt-5">
                    <div className="col text-end">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleNext}
                      >
                        Próximo
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

{step === 2 && (
            <div>
              <h3>Confirmar SMS</h3>
              <p>
                Enviamos um SMS ao seu celular. Clique em “Confirmei e prosseguir”
                após receber e validar a mensagem.
              </p>
              {simulationLoading ? (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              ) : (
                <div className="row mt-4">
                  <div className="col text-start">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handlePrevious}
                    >
                      Anterior
                    </button>
                  </div>
                  <div className="col text-end">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleConfirmSms}
                    >
                      Confirmei e prosseguir
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}


          {step === 3 && (
            <div>
              <h3>Simulação</h3>
              {simulationLoading ? (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p>Carregando simulação...</p>
                </div>
              ) : (
                <>
                  {simulationOptions.length > 0 ? (
                    <div className="form-group mb-3">
                      <label>Selecione uma opção de simulação</label>
                      <select
                        className="form-control"
                        onChange={handleSimulationSelect}
                        defaultValue=""
                      >
                        <option value="" disabled>
                          Selecione uma opção
                        </option>
                        {simulationOptions.map((option, index) => (
                          <option key={index} value={index}>
                            {option.quantidadeParcelas} parcelas - Valor da Parcela: {option.valorParcela}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <p>Nenhuma opção de simulação encontrada.</p>
                  )}
                  <div className="row mt-5">
                    <div className="col text-start">
                      <button type="button" className="btn btn-secondary" onClick={handlePrevious}>
                        Anterior
                      </button>
                    </div>
                    <div className="col text-end">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleNext}
                        disabled={!selectedSimulation}
                      >
                        Próximo
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {step === 4 && (
            <div>
              <h3>Endereço e Dados Pessoais</h3>
              <div className="form-group mb-3">
                <label>CEP</label>
                <InputMask
                  mask="99999-999"
                  name="cdCepRes"
                  className="form-control"
                  value={formData.cdCepRes}
                  onChange={handleCepChange}
                  required
                />
              </div>
              <div className="form-group mb-3">
                <label>Endereço</label>
                <input
                  type="text"
                  name="enderecoRes"
                  className="form-control"
                  value={formData.enderecoRes}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group mb-3">
                <label>Bairro</label>
                <input
                  type="text"
                  name="bairroRes"
                  className="form-control"
                  value={formData.bairroRes}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="row mb-3">
                <div className="form-group col-md-4">
                  <label>Número</label>
                  <input
                    type="text"
                    name="numeroRes"
                    className="form-control"
                    value={formData.numeroRes}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group col-md-4">
                  <label>Cidade</label>
                  <input
                    type="text"
                    name="cidadeRes"
                    className="form-control"
                    value={formData.cidadeRes}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group col-md-4">
                  <label>Estado (UF)</label>
                  <input
                    type="text"
                    name="ufRes"
                    className="form-control"
                    value={formData.ufRes}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="row mt-5">
                <div className="col text-start">
                  <button type="button" className="btn btn-secondary" onClick={handlePrevious}>
                    Anterior
                  </button>
                </div>
                <div className="col text-end">
                  <button type="button" className="btn btn-primary" onClick={handleNext}>
                    Próximo
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <h3>Informações Bancárias</h3>
              <div className="form-row row">
                <div className="form-group mb-3 col-4">
                  <label>Banco</label>
                  <select
                    name="cdBanco"
                    className="form-control"
                    value={formData.cdBanco}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione um banco</option>
                    {bancos.map((banco) => (
                      <option key={banco.value} value={banco.value}>
                        {banco.value} - {banco.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group mb-3 col-4">
                  <label>Agência</label>
                  <input
                    type="text"
                    name="cdAgencia"
                    className="form-control"
                    value={formData.cdAgencia}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group mb-3 col-4">
                  <label>Conta</label>
                  <input
                    type="text"
                    name="cdConta"
                    className="form-control"
                    value={formData.cdConta}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group mb-3">
                  <label>Valor da Renda</label>
                  <input
                    type="number"
                    name="valorRenda"
                    className="form-control"
                    value={formData.valorRenda}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="row mt-5">
                  <div className="col text-start">
                    <button type="button" className="btn btn-secondary" onClick={handlePrevious}>
                      Anterior
                    </button>
                  </div>
                  <div className="col text-end">
                    <button type="button" className="btn btn-primary" onClick={handleNext}>
                      Próximo
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 6 && (
            <div>
              <h3>Documentos</h3>
              <div className="form-group mb-3">
                <label>Nome da Mãe</label>
                <input
                  type="text"
                  name="nomeMae"
                  className="form-control"
                  value={formData.nomeMae}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="row mb-3">
                <div className="form-group col-md-4">
                  <label>RG</label>
                  <InputMask
                    mask="99.999.999-9"
                    name="cdRg"
                    className="form-control"
                    value={formData.cdRg}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group col-md-4">
                  <label>UF Emissor do RG</label>
                  <input
                    type="text"
                    name="ufEmissorRg"
                    className="form-control"
                    value={formData.ufEmissorRg}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group col-md-4">
                  <label>Emissor do RG</label>
                  <input
                    type="text"
                    name="emissorRg"
                    className="form-control"
                    value={formData.emissorRg}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="form-group mb-3">
                <label>Data de Expedição do RG</label>
                <input
                  type="date"
                  name="dataExpedicaoRg"
                  className="form-control"
                  value={formData.dataExpedicaoRg}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="row mb-3">
                <div className="form-group col-md-6">
                  <label>Sexo</label>
                  <select
                    name="sexo"
                    className="form-control"
                    value={formData.sexo}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione</option>
                    <option value="M">Masculino</option>
                    <option value="F">Feminino</option>
                  </select>
                </div>
                <div className="form-group col-md-6">
                  <label>Estado Civil</label>
                  <select
                    name="estadoCivil"
                    className="form-control"
                    value={formData.estadoCivil}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione</option>
                    <option value="Solteiro">Solteiro</option>
                    <option value="Casado">Casado</option>
                    <option value="Divorciado">Divorciado</option>
                    <option value="Viúvo">Viúvo</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
              </div>
              <div className="row mb-3">
                <div className="form-group col-md-6">
                  <label>Naturalidade</label>
                  <input
                    type="text"
                    name="naturalidade"
                    className="form-control"
                    value={formData.naturalidade}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>Nacionalidade</label>
                  <input
                    type="text"
                    name="nacionalidade"
                    className="form-control"
                    value={formData.nacionalidade}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="row mt-5">
                <div className="col text-start">
                  <button type="button" className="btn btn-secondary" onClick={handlePrevious}>
                    Anterior
                  </button>
                </div>
                <div className="col text-end">
                  <button type="submit" className="btn btn-success">
                    Enviar
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Modal de Erro */}
      <ErrorModal
        show={errorModal.show}
        onClose={() => setErrorModal({ ...errorModal, show: false })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
}

export default MultiStepForm;
